// CSSConflictResolver.js
// Enterprise-grade CSS conflict resolution system
// Eliminates !important rule chaos and establishes single source of truth

/**
 * CSS Conflict Resolver
 * Systematically resolves CSS conflicts identified in investigation
 * Establishes enterprise-grade CSS architecture
 */
export class CSSConflictResolver {
    static _instance = null;
    static _resolved = false;
    
    /**
     * Singleton pattern for consistent CSS management
     */
    static getInstance() {
        if (!CSSConflictResolver._instance) {
            CSSConflictResolver._instance = new CSSConflictResolver();
        }
        return CSSConflictResolver._instance;
    }
    
    constructor() {
        if (CSSConflictResolver._instance) {
            return CSSConflictResolver._instance;
        }
        
        this.conflictingRules = [];
        this.resolvedRules = new Map();
        
        CSSConflictResolver._instance = this;
    }
    
    /**
     * Resolve all CSS conflicts identified in investigation
     * Must be called before any game launches
     */
    static resolveAllConflicts() {
        if (CSSConflictResolver._resolved) {
            console.log('✅ CSS conflicts already resolved');
            return;
        }
        
        console.log('🔧 CSSConflictResolver: Resolving all CSS conflicts...');
        
        // Phase 1A: Remove conflicting !important rules
        CSSConflictResolver._removeConflictingImportantRules();
        
        // Phase 1B: Establish unified game container positioning
        CSSConflictResolver._establishUnifiedGameContainerRules();
        
        // Phase 1C: Fix canvas positioning conflicts
        CSSConflictResolver._fixCanvasPositioningConflicts();
        
        // Phase 1D: Resolve overlay z-index hierarchy
        CSSConflictResolver._resolveOverlayZIndexHierarchy();
        
        CSSConflictResolver._resolved = true;
        console.log('✅ All CSS conflicts resolved');
    }
    
    /**
     * Remove conflicting !important rules that break game positioning
     * @private
     */
    static _removeConflictingImportantRules() {
        console.log('🔧 Removing conflicting !important rules...');
        
        // Inject CSS to override conflicting !important rules
        const conflictResolutionCSS = `
            <style id="css-conflict-resolution" data-priority="highest">
                /* ENTERPRISE CSS CONFLICT RESOLUTION */
                /* Overrides all conflicting !important rules with higher specificity */
                
                /* Game Container - UNIFIED POSITIONING */
                #game-container {
                    /* Remove all conflicting positioning */
                    position: absolute !important;
                    top: 50px !important;
                    left: 0 !important;
                    width: 100% !important;
                    height: calc(100% - 50px) !important;
                    z-index: 1 !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    overflow: hidden !important;
                    background: transparent !important;
                    /* Allow JavaScript overrides when needed */
                    transition: none !important;
                }
                
                /* Canvas - UNIFIED RENDERING */
                #game-container canvas {
                    position: relative !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 100% !important;
                    height: 100% !important;
                    z-index: 1 !important;
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                    transform: none !important;
                    backface-visibility: hidden !important;
                    /* Ensure proper rendering */
                    image-rendering: -webkit-optimize-contrast !important;
                }
                
                /* Override mobile contamination */
                @media (max-width: 768px) {
                    #game-container {
                        /* Mobile gets different rules but no conflicts */
                        top: 0 !important;
                        height: 100vh !important;
                        padding-bottom: 88px !important;
                    }
                }
                
                /* Desktop specific overrides */
                @media (min-width: 769px) {
                    #game-container {
                        /* Desktop maintains original PPT Hero expectations */
                        top: 50px !important;
                        height: calc(100% - 50px) !important;
                        padding-bottom: 0 !important;
                    }
                }
                
                /* Overlay Z-Index Hierarchy - UNIFIED */
                .overlay, [id*="overlay"] {
                    z-index: 10000 !important;
                }
                
                .notification-container {
                    z-index: 50000 !important;
                }
                
                #preloader, #loadingStatus {
                    z-index: 99999 !important;
                }
                
                /* Ensure overlays don't interfere with games */
                body.game-active .overlay:not(.notification-container) {
                    display: none !important;
                }
                
                /* Top ribbon positioning - PPT Hero dependency */
                #top-ribbon {
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    right: 0 !important;
                    height: 50px !important;
                    z-index: 100 !important;
                    display: none !important; /* Hidden by default, shown by games */
                }
                
                /* Authenticated dashboard overrides */
                body.authenticated-dashboard-active #game-container {
                    display: none !important;
                    visibility: hidden !important;
                }
                
                body.authenticated-dashboard-active #top-ribbon {
                    display: none !important;
                }
            </style>
        `;
        
        // Insert at highest priority (beginning of head)
        document.head.insertBefore(
            document.createRange().createContextualFragment(conflictResolutionCSS).firstElementChild,
            document.head.firstChild
        );
        
        console.log('✅ Conflicting !important rules resolved');
    }
    
    /**
     * Establish unified game container positioning rules
     * @private
     */
    static _establishUnifiedGameContainerRules() {
        console.log('🔧 Establishing unified game container rules...');
        
        // Set body class to indicate game state
        const gameStateCSS = `
            <style id="game-state-management">
                /* ENTERPRISE GAME STATE MANAGEMENT */
                
                /* Default state - DOM scenes active */
                body:not(.game-active) #game-container {
                    display: none !important;
                    visibility: hidden !important;
                }
                
                /* Game active state */
                body.game-active #game-container {
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                }
                
                /* Game active - hide DOM overlays */
                body.game-active [id*="overlay"]:not(.notification-container) {
                    display: none !important;
                }
                
                /* Game active - show top ribbon */
                body.game-active #top-ribbon {
                    display: flex !important;
                }
            </style>
        `;
        
        document.head.appendChild(
            document.createRange().createContextualFragment(gameStateCSS).firstElementChild
        );
        
        console.log('✅ Unified game container rules established');
    }
    
    /**
     * Fix canvas positioning conflicts
     * @private
     */
    static _fixCanvasPositioningConflicts() {
        console.log('🔧 Fixing canvas positioning conflicts...');
        
        // Remove any existing canvas style overrides that conflict
        const canvasElements = document.querySelectorAll('canvas');
        canvasElements.forEach(canvas => {
            // Remove inline styles that might conflict
            canvas.style.position = '';
            canvas.style.top = '';
            canvas.style.left = '';
            canvas.style.transform = '';
            canvas.style.zIndex = '';
        });
        
        console.log('✅ Canvas positioning conflicts resolved');
    }
    
    /**
     * Resolve overlay z-index hierarchy
     * @private
     */
    static _resolveOverlayZIndexHierarchy() {
        console.log('🔧 Resolving overlay z-index hierarchy...');
        
        // Establish clear z-index hierarchy
        const zIndexHierarchy = {
            'game-container': 1,
            'top-ribbon': 100,
            'overlay': 10000,
            'notification-container': 50000,
            'preloader': 99999
        };
        
        Object.entries(zIndexHierarchy).forEach(([selector, zIndex]) => {
            const elements = document.querySelectorAll(`#${selector}, .${selector}`);
            elements.forEach(element => {
                element.style.zIndex = zIndex;
            });
        });
        
        console.log('✅ Z-index hierarchy resolved');
    }
    
    /**
     * Check if CSS conflicts are resolved
     * @returns {boolean} - Whether conflicts are resolved
     */
    static areConflictsResolved() {
        return CSSConflictResolver._resolved;
    }
    
    /**
     * Reset CSS conflict resolution (for debugging)
     */
    static resetResolution() {
        const resolutionStyles = document.querySelectorAll('#css-conflict-resolution, #game-state-management');
        resolutionStyles.forEach(style => style.remove());
        CSSConflictResolver._resolved = false;
        console.log('🔄 CSS conflict resolution reset');
    }
}

// Auto-resolve conflicts on import
CSSConflictResolver.resolveAllConflicts();

export default CSSConflictResolver;

